// App.tsx - Main application routing - Performance optimized with lazy loading
import { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Providers } from './providers';
import { BottomNav } from './components/navigation/BottomNav';
import { ScrollToTop } from './components/navigation/ScrollToTop';
import { ProtectedRoute } from './components/guards/ProtectedRoute';
import { BlockedUserGate } from './components/guards/BlockedUserGate';
import { BlockedGuestGate } from './components/guards/BlockedGuestGate';

import { OrgOnboardingRoute } from './components/guards/OrgOnboardingRoute';
import { useAuth } from './hooks/useAuth';
import { useTradeCart } from './hooks/useTradeCart';
import { PWAConfigProvider } from './contexts/PWAConfigContext';
import { PendingSwishWatcher } from './components/PendingSwishWatcher';
import { initWebVitals } from './lib/performance';
import { useDynamicManifest } from './hooks/useDynamicManifest';
import { AddToCartAnimation } from './components/animations/AddToCartAnimation';
import { AddToFavoriteAnimation } from './components/animations/AddToFavoriteAnimation';
import { isSlugPath, normalizePath } from './lib/routeConfig';
import { preloadStripeGlobal } from './hooks/useStripePreload';
import { ErrorBoundary } from './components/utils/ErrorBoundary';
import { SystemBanner } from './components/system/SystemBanner';

// Minimal loading fallback - inline to avoid blocking render
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// ============================================================
// LAZY-LOADED PAGES - Only loaded when route is accessed
// ============================================================

// Critical pages - preloaded on app start
const HomePage = lazy(() => import('./pages/HomePage'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const AuthPage = lazy(() => import('./pages/AuthPage'));
const RootLandingPage = lazy(() => import('./pages/RootLandingPage'));

// Secondary pages - loaded on demand
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const BlockedPage = lazy(() => import('./pages/BlockedPage'));
const ModuleSettingsPage = lazy(() => import('./pages/ModuleSettingsPage'));
const ModuleDetailPage = lazy(() => import('./pages/ModuleDetailPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const MyTicketsPage = lazy(() => import('./pages/MyTicketsPage'));
const TicketChatPage = lazy(() => import('./pages/TicketChatPage'));
const PaymentPage = lazy(() => import('./pages/PaymentPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const PickupSuccessPage = lazy(() => import('./pages/PickupSuccessPage'));
const ReturnSuccessPage = lazy(() => import('./pages/ReturnSuccessPage'));
const TopUpPage = lazy(() => import('./pages/TopUpPage'));
const DeviceShopPage = lazy(() => import('./pages/DeviceShopPage'));

const BankIdCallbackPage = lazy(() => import('./pages/BankIdCallbackPage'));
const OrderHistoryPage = lazy(() => import('./pages/OrderHistoryPage'));
const UserSettingsPage = lazy(() => import('./pages/UserSettingsPage'));
const DeviceManagementPage = lazy(() => import('./pages/DeviceManagementPage'));
const LoyaltyPage = lazy(() => import('./pages/LoyaltyPage'));
const DebugPage = lazy(() => import('./pages/DebugPage'));
const OrgLandingPage = lazy(() => import('./pages/OrgLandingPage'));
const OrgOnboardingPage = lazy(() => import('./pages/OrgOnboardingPage'));
const OrgJoinRedirect = lazy(() => import('./pages/OrgJoinRedirect'));
const PreviewPage = lazy(() => import('./pages/PreviewPage'));
const FortnoxCallbackPage = lazy(() => import('./pages/FortnoxCallbackPage'));
const SecurityPrivacyPage = lazy(() => import('./pages/SecurityPrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));

function AppRoutes() {
  const location = useLocation();
  const { user, isGuest } = useAuth();
  const { deviceId } = useTradeCart();

  const currentPath = normalizePath(location.pathname);
  const isSlugRoute = isSlugPath(currentPath);

  // Global scroll lock for slug landing page - locks html/body completely
  useEffect(() => {
    if (!isSlugRoute) return;

    const scrollY = window.scrollY;
    const html = document.documentElement;
    const body = document.body;

    // Lock html
    html.style.overflow = 'hidden';
    html.style.overscrollBehaviorY = 'none';
    
    // Lock body with position fixed (required for iOS)
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overscrollBehaviorY = 'none';

    return () => {
      // Cleanup - restore scroll
      html.style.overflow = '';
      html.style.overscrollBehaviorY = '';
      body.style.overflow = '';
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.overscrollBehaviorY = '';
      window.scrollTo(0, scrollY);
    };
  }, [isSlugRoute]);

  // Failsafe: Unregister any active Service Workers on mount
  useEffect(() => {
    (async () => {
      if ('serviceWorker' in navigator) {
        try {
          const regs = await navigator.serviceWorker.getRegistrations();
          if (regs.length > 0) {
            console.log('[AppRoutes] 🧹 Cleaning up', regs.length, 'active Service Worker(s)...');
            await Promise.all(regs.map(r => r.unregister()));
            console.log('[AppRoutes] ✅ Service Workers unregistered');
          } else {
            console.log('[AppRoutes] ✅ No active Service Workers detected');
          }
        } catch (err) {
          console.error('[AppRoutes] ⚠️ SW cleanup failed:', err);
        }
      }
    })();
  }, []);

  // Clean up expired BankID orderRefs on app start (3 min timeout)
  useEffect(() => {
    const orderRefTimestamp = sessionStorage.getItem('bankid_orderRef_timestamp');
    const orderRef = sessionStorage.getItem('bankid_orderRef');
    
    if (orderRef && orderRefTimestamp) {
      const age = Date.now() - parseInt(orderRefTimestamp, 10);
      // BankID orders expire after ~3 minutes
      if (age > 180000) {
        sessionStorage.removeItem('bankid_orderRef');
        sessionStorage.removeItem('bankid_orderRef_timestamp');
        localStorage.removeItem('bankid_orderRef');
        localStorage.removeItem('bankid_orderRef_timestamp');
        console.log('[AppRoutes] 🧹 Cleared expired BankID orderRef (age:', Math.round(age/1000), 's)');
      }
    }
  }, []);

  // Diagnostics: log every path change
  useEffect(() => {
    console.log('[AppRoutes] PATH =', location.pathname);
  }, [location.pathname]);

  // Initialize Web Vitals monitoring
  useEffect(() => {
    initWebVitals();
  }, []);

  // Setup dynamic PWA manifest with organization branding
  useDynamicManifest();

  // Preload Stripe.js at app startup (low priority, doesn't block render)
  useEffect(() => {
    if (deviceId) {
      preloadStripeGlobal(deviceId);
    }
  }, [deviceId]);

  // Show BottomNav only when authenticated and not on auth/onboarding/payment flows
  const excludedPaths = ['/auth', '/bankid-callback', '/onboarding', '/payment', '/payment-success', '/blocked'];
  const alwaysShowNavPaths = ['/my-tickets', '/contact', '/orders'];
  const isOrgOnboarding = currentPath.match(/^\/[^/]+\/onboarding/);
  const showBottomNav = (
    ((!!user || isGuest) &&
      !isSlugRoute &&
      !isOrgOnboarding &&
      !excludedPaths.includes(currentPath) &&
      !currentPath.startsWith('/payment') &&
      !currentPath.startsWith('/topup'))
    || alwaysShowNavPaths.includes(currentPath)
  );

  return (
    <PWAConfigProvider deviceId={deviceId}>
      <BlockedUserGate>
      <BlockedGuestGate>
        <ScrollToTop />
        
        {!isSlugRoute && <PendingSwishWatcher />}
        
        <div className="fixed inset-0 bg-gradient-to-br from-[hsl(var(--app-bg-from))] via-[hsl(var(--app-bg-via))] to-[hsl(var(--app-bg-to))] -z-10 transition-colors duration-500" />
        
        <AddToCartAnimation />
        <AddToFavoriteAnimation />
        
        <div className={isSlugRoute ? "fixed inset-0 overflow-hidden" : "pb-[calc(84px+env(safe-area-inset-bottom))]"}>
          {showBottomNav && <SystemBanner />}
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* PUBLIC ROUTES - NO AUTH REQUIRED */}
              <Route path="/blocked" element={<BlockedPage />} />
            <Route path="/pwa-preview" element={<PreviewPage />} />
            <Route path="/bankid-callback" element={<BankIdCallbackPage />} />
            <Route path="/settings/integrations/fortnox/callback" element={<FortnoxCallbackPage />} />
            <Route path="/join/:orgSlug" element={<OrgJoinRedirect />} />
            <Route path="/auth" element={<AuthPage />} />
            
            {/* ROOT LANDING PAGE */}
            <Route path="/" element={<RootLandingPage />} />
            
            {/* PROTECTED ROUTES - AUTH REQUIRED */}
            <Route path="/home" element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            } />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/account" element={
              <ProtectedRoute>
                <AccountPage />
              </ProtectedRoute>
            } />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/my-tickets" element={
              <ProtectedRoute>
                <MyTicketsPage />
              </ProtectedRoute>
            } />
            <Route path="/my-tickets/:ticketId" element={
              <ProtectedRoute>
                <TicketChatPage />
              </ProtectedRoute>
            } />
            <Route path="/settings/modules" element={
              <ProtectedRoute>
                <ModuleSettingsPage />
              </ProtectedRoute>
            } />
            <Route path="/settings/modules/:moduleId" element={
              <ProtectedRoute>
                <ModuleDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/payment" element={
              <ErrorBoundary>
                <PaymentPage />
              </ErrorBoundary>
            } />
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/pickup-success" element={<PickupSuccessPage />} />
            <Route path="/return-success" element={<ReturnSuccessPage />} />
            <Route path="/topup" element={
              <ProtectedRoute>
                <ErrorBoundary>
                  <TopUpPage />
                </ErrorBoundary>
              </ProtectedRoute>
            } />
            <Route path="/orders" element={
              <ProtectedRoute>
                <OrderHistoryPage />
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <UserSettingsPage />
              </ProtectedRoute>
            } />
            <Route path="/devices" element={
              <ProtectedRoute>
                <DeviceManagementPage />
              </ProtectedRoute>
            } />
            <Route path="/loyalty" element={
              <ProtectedRoute>
                <LoyaltyPage />
              </ProtectedRoute>
            } />
            <Route path="/debug" element={
              <ProtectedRoute>
                <DebugPage />
              </ProtectedRoute>
            } />
            <Route path="/security" element={
              <ProtectedRoute>
                <SecurityPrivacyPage />
              </ProtectedRoute>
            } />
            {/* Publika: länkas från villkorstexten under BankID-knapparna, innan användaren har ett konto */}
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/shop/:deviceCode" element={<DeviceShopPage />} />
            
            {/* ORG SLUG ROUTES */}
            <Route path="/:orgSlug/onboarding" element={
              <OrgOnboardingRoute>
                <OrgOnboardingPage />
              </OrgOnboardingRoute>
            } />
            <Route path="/:orgSlug" element={<OrgLandingPage />} />
            
            {/* 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
        </div>
        {showBottomNav && <BottomNav />}
      </BlockedGuestGate>
      </BlockedUserGate>
    </PWAConfigProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Providers>
        <AppRoutes />
      </Providers>
    </ErrorBoundary>
  );
}

export default App;
